import type { $Fetch } from 'nitropack';
import type { BFFResponse } from '~/types/BFFResponse';
import type { Address, Customer, LineItem, Me, Order, ShippingMethod, ShoppingList } from '~/types/GraphQL';

export default ($fetch: $Fetch) => {
  const getMe = async () => $fetch<BFFResponse<Me>>('/me');

  // TODO reminder remove this route in next release after migration
  const migrateSession = async () => $fetch<BFFResponse<Me>>('/me/migrate/session');

  const getAddresses = async () => $fetch<BFFResponse<Address[]>>('/me/addresses');

  const addAddress = async (body: any) => $fetch<BFFResponse<Customer>>('/me/addresses', { method: 'POST', body });

  const updateAddress = async (id: string, body: any) => $fetch<BFFResponse<Customer>>(`/me/addresses/${id}`, { method: 'PUT', body });

  const removeAddress = async (id: string, body: { version: number }) => $fetch<BFFResponse<{ id: string; version: number }>>(`/me/addresses/${id}`, { method: 'DELETE', body });

  const updateBirthDate = async (body: any) => $fetch<BFFResponse<Customer>>('/me/profile/birthDate', { method: 'PUT', body });

  const synchronizeUserWithCRM = async () => $fetch<BFFResponse<never>>('/me/loyalty/sync');

  const validateLoyaltyDiscountCode = async () => $fetch<BFFResponse<unknown>>('/me/loyalty/validateDiscount');

  const applyLoyaltyMembership = async (cardNumber?: string, newRegistration?: boolean) => $fetch<BFFResponse>(`/me/loyalty/register${cardNumber ? '/' + cardNumber : ''}`, { method: 'POST', query: { newRegistration }, body: {} });

  const unregisterLoyaltyMembership = async () => $fetch<BFFResponse<unknown>>('/me/loyalty/unregister', { method: 'DELETE' });

  const getAddressFromCRM = async () => $fetch<BFFResponse<Address>>('/me/loyalty/address');

  const userTransactionCount = async () => $fetch<BFFResponse<number>>('/me/loyalty/transactionsCount');

  const createWishlist = async () => $fetch<BFFResponse<ShoppingList>>('/me/wishlists', { method: 'POST', body: {} });

  const getWishlist = async () => $fetch<BFFResponse<ShoppingList>>('/me/wishlist');

  const addItemToWishlist = async (id: string, body: any) => $fetch<BFFResponse<ShoppingList>>(`/me/wishlist/${id}`, { method: 'PUT', body });

  const removeItemFromWishlist = async (id: string, itemId: string, body: any) => $fetch<BFFResponse<ShoppingList>>(`/me/wishlist/${id}/${itemId}`, { method: 'DELETE', body });

  const signIn = async (payload: { email: string; password: string; rememberMe: boolean }) => (
    $fetch<BFFResponse<unknown>>('/customers/legacyLogin', {
      method: 'POST',
      body: {
        email: payload.email,
        password: payload.password,
        rememberMe: payload.rememberMe,
      },
    })
  );

  const isLoggedIn = async () => $fetch<BFFResponse<unknown>>('/me/isLoggedIn');

  const requestPassword = async (email: string) => $fetch<BFFResponse<unknown>>('/me/password/request', { method: 'POST', body: { email: encodeURIComponent(email) } });

  const resetPassword = async ({ newPassword, token }: { newPassword: string; token: string }) => $fetch<BFFResponse<unknown>>('/me/password/reset', { method: 'POST', body: { password: newPassword, token } });

  const getMyOrder = async (orderNumber: string, withCustomFields: boolean, withExternalId: boolean) => $fetch<BFFResponse<Order>>(`/me/orders/${orderNumber}`, { query: { withCustomFields, withExternalId } });

  const getMyOrders = (limit = 20, offset = 0) => $fetch<BFFResponse<Order[]>>(`/me/orders`, { query: { limit, offset } });

  const getMyReturns = async (limit = 20, offset = 0) => $fetch<BFFResponse<Order[]>>(`/me/rma`, { query: { limit, offset } });

  const getMyReturn = async (orderId: string) => $fetch<BFFResponse<Order>>(`/me/orders/${orderId}/rma`);

  const getMyOrderInvoice = async (orderId: string, orderNumber: string) => $fetch<BFFResponse<Blob>>(`/me/orders/${orderId}/invoices`, { query: { orderNumber }, headers: { Accept: 'application/pdf' } });

  const getReturnLabel = async <T>(orderId: string, returnTrackingId: string) => $fetch<BFFResponse<T>>(`/me/rma/${orderId}/${returnTrackingId}/label`);

  const cancelReturn = async <T>(orderId: string, returnTrackingId: string, orderNumber: string) => $fetch<BFFResponse<T>>(`/me/orders/${orderId}/rma/${returnTrackingId}`, { method: 'DELETE', body: { orderNumber } });

  const createReturn = async <T>(orderId: string, orderNumber: string, lineItems: LineItem[]) => $fetch<BFFResponse<T>>(`/me/orders/${orderId}/rma`, { method: 'POST', body: { orderNumber, lineItems } });

  const changePassword = async (body: { version: number; currentPassword: string; newPassword: string }) => $fetch<BFFResponse<unknown>>('/me/password', { method: 'PUT', body });

  const updateProfile = async (body: { version: number; dateOfBirth: string; lastName: string; salutation: string; title: string }) => $fetch<BFFResponse<Customer>>('/me/update', { method: 'PUT', body });

  const getShippingMethodsByCart = async (id: string | undefined) => $fetch<BFFResponse<ShippingMethod[]>>(`/me/cart/${id}/shippingMethods`);

  return {
    getMe,
    migrateSession,
    // Addresses
    getAddresses,
    addAddress,
    updateAddress,
    removeAddress,
    updateBirthDate,
    getShippingMethodsByCart,
    // Loyalty
    synchronizeUserWithCRM,
    validateLoyaltyDiscountCode,
    applyLoyaltyMembership,
    unregisterLoyaltyMembership,
    getAddressFromCRM,
    userTransactionCount,
    // Wishlist
    createWishlist,
    getWishlist,
    addItemToWishlist,
    removeItemFromWishlist,

    // Login
    signIn,
    isLoggedIn,
    requestPassword,
    resetPassword,

    // Orders
    getMyOrder,
    getMyOrders,
    getMyReturn,
    getMyReturns,
    getReturnLabel,
    getMyOrderInvoice,
    cancelReturn,
    createReturn,

    // My Profile
    changePassword,
    updateProfile,
  };
};
